import { formatWhatsApp } from "@baze/schema";
import type { RenderContext, SectionProps } from "../context";
import { domId, messageVars } from "../context";
import { groupedHours, mailHref, socialLinks, telHref, waLink } from "../helpers";
import { Icon } from "../icons";
import { Container, MultiLine, SectionShell, SiteLink, cx } from "../ui";

function Socials({ ctx, className }: { ctx: RenderContext; className?: string }) {
  const list = socialLinks(ctx.site);
  if (list.length === 0) return null;
  return (
    <ul className={cx("flex flex-wrap gap-2", className)}>
      {list.map((s) => (
        <li key={s.kind}>
          <SiteLink
            ctx={ctx}
            href={s.href}
            ariaLabel={`${s.label} ${ctx.site.name}`}
            className="grid size-11 place-items-center rounded-full border border-site-border text-site-text transition hover:border-site-primary hover:bg-site-primary hover:text-site-on-primary"
          >
            <Icon name={s.kind} className="size-[1.125rem]" />
          </SiteLink>
        </li>
      ))}
    </ul>
  );
}

function Copyright({ ctx }: { ctx: RenderContext }) {
  return (
    <p className="text-sm text-site-muted">
      © {ctx.year} {ctx.site.name}. Semua hak dilindungi.
    </p>
  );
}

const colHeading = "text-xs font-semibold tracking-[0.16em] text-site-muted uppercase";
const colLink = "inline-flex min-h-9 items-center gap-2 text-[0.9375rem] text-site-text transition-colors hover:text-site-primary";

export function Footer({ node, ctx }: SectionProps<"footer">) {
  const c = node.content;
  const site = ctx.site;
  const blurb = c.text || site.tagline;
  const blurbPath = c.text ? "content.text" : undefined;
  const showSocial = c.showSocial !== false;

  if (node.variant === "columns") {
    const wa = waLink(site.contact.whatsapp, site.whatsapp?.defaultMessage, messageVars(ctx));
    const tel = telHref(site.contact.phone);
    const mail = mailHref(site.contact.email);
    const hours = c.showHours !== false ? groupedHours(site.hours) : [];
    const { address: street, city } = site.contact;
    const address = c.showAddress !== false ? (street && city && !street.includes(city) ? `${street}, ${city}` : (street ?? city ?? "")) : "";
    const hasContact = !!(wa || tel || mail || address);
    const idContact = domId(ctx, `${node.id}-kontak`);
    const idHours = domId(ctx, `${node.id}-jam`);
    const idNav = domId(ctx, `${node.id}-nav`);
    return (
      <SectionShell ctx={ctx} node={node} as="footer" padded={false} className="border-t border-site-border pt-16 pb-8 @3xl:pt-20">
        <Container>
          <div className="grid gap-12 @2xl:grid-cols-2 @5xl:grid-cols-[1.4fr_1fr_1fr_0.8fr] @5xl:gap-10">
            <div className="max-w-sm">
              <p className="baze-heading text-2xl">{site.name}</p>
              {blurb ? <MultiLine ctx={ctx} text={blurb} path={blurbPath} className="mt-3 leading-relaxed text-site-muted" /> : null}
              {showSocial ? <Socials ctx={ctx} className="mt-6" /> : null}
            </div>
            {hasContact ? (
              <div>
                <h2 id={idContact} className={colHeading}>
                  Kontak
                </h2>
                <ul aria-labelledby={idContact} className="mt-4 space-y-1.5">
                  {wa && site.contact.whatsapp ? (
                    <li>
                      <SiteLink ctx={ctx} href={wa} whatsapp className={colLink}>
                        <Icon name="whatsapp" className="size-4 text-site-muted" />
                        {formatWhatsApp(site.contact.whatsapp)}
                      </SiteLink>
                    </li>
                  ) : null}
                  {tel ? (
                    <li>
                      <SiteLink ctx={ctx} href={tel} className={colLink}>
                        <Icon name="phone" className="size-4 text-site-muted" />
                        {site.contact.phone}
                      </SiteLink>
                    </li>
                  ) : null}
                  {mail ? (
                    <li>
                      <SiteLink ctx={ctx} href={mail} className={colLink}>
                        <Icon name="mail" className="size-4 text-site-muted" />
                        {site.contact.email}
                      </SiteLink>
                    </li>
                  ) : null}
                  {address ? (
                    <li className="flex gap-2 pt-1.5 text-[0.9375rem] leading-relaxed text-site-text">
                      <Icon name="map-pin" className="mt-1 size-4 shrink-0 text-site-muted" />
                      <address className="not-italic">{address}</address>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
            {hours.length > 0 ? (
              <div>
                <h2 id={idHours} className={colHeading}>
                  Jam buka
                </h2>
                <ul aria-labelledby={idHours} className="mt-4 space-y-2 text-[0.9375rem]">
                  {hours.map((g) => (
                    <li key={g.label} className="flex justify-between gap-4 @2xl:max-w-64">
                      <span className="text-site-muted">{g.label}</span>
                      <span className="text-site-text tabular-nums">{g.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {ctx.nav.length > 0 ? (
              <nav aria-labelledby={idNav}>
                <h2 id={idNav} className={colHeading}>
                  Navigasi
                </h2>
                <ul className="mt-4 space-y-1">
                  {ctx.nav.map((l) => (
                    <li key={l.href}>
                      <SiteLink ctx={ctx} href={l.href} className={colLink}>
                        {l.label}
                      </SiteLink>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}
          </div>
          <div className="mt-14 border-t border-site-border pt-6">
            <Copyright ctx={ctx} />
          </div>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node} as="footer" padded={false} className="border-t border-site-border py-12 @3xl:py-16">
      <Container className="flex flex-col items-center text-center">
        <p className="baze-heading text-2xl">{site.name}</p>
        {blurb ? <MultiLine ctx={ctx} text={blurb} path={blurbPath} className="mt-3 max-w-md leading-relaxed text-site-muted" /> : null}
        {ctx.nav.length > 0 ? (
          <nav aria-label="Navigasi footer" className="mt-6">
            <ul className="flex flex-wrap justify-center gap-x-2">
              {ctx.nav.map((l) => (
                <li key={l.href}>
                  <SiteLink ctx={ctx} href={l.href} className="inline-flex min-h-11 items-center px-2 text-sm font-medium text-site-muted hover:text-site-text">
                    {l.label}
                  </SiteLink>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        {showSocial ? <Socials ctx={ctx} className="mt-6 justify-center" /> : null}
        {c.showAddress !== false && site.contact.address ? (
          <address className="mt-6 text-sm text-site-muted not-italic">{site.contact.address}</address>
        ) : null}
        {c.showHours !== false && site.hours?.length ? (
          <p className="mt-2 flex flex-wrap justify-center gap-x-3 text-sm text-site-muted">
            {groupedHours(site.hours).map((g) => (
              <span key={g.label}>
                {g.label}: <span className="tabular-nums">{g.value}</span>
              </span>
            ))}
          </p>
        ) : null}
        <div className="mt-8">
          <Copyright ctx={ctx} />
        </div>
      </Container>
    </SectionShell>
  );
}

